import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import SendIcon from '@mui/icons-material/Send';
import { useApiClient } from '../../ApiClientProvider';
import { PodService } from '../../services/podService';
import { getPodHubService } from '../../services/podHubService';
import type { PodMessageCreatedPayload } from '../../services/podHubService';
import { IPodMessage, IPodResponse } from '../../interfaces/ICommunityHub';
import { clearPodUnreadCount } from '../../redux/podsSlice';
import { addFocusedPod, removeFocusedPod } from '../../services/podReadFocusTracker';
import ChatMessage from './chat/ChatMessage';
import { getAvatarGradient } from '../../helpers/avatarColor';

function normaliseMessage(raw: unknown): IPodMessage {
  const obj = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    id: String(obj.id ?? obj.Id ?? ''),
    senderId: obj.sender_id as string ?? obj.senderId as string ?? null,
    displayName: obj.display_name as string ?? obj.displayName as string ?? null,
    avatarUrl: obj.avatar_url as string ?? obj.avatarUrl as string ?? null,
    type: String(obj.type ?? 'text').toLowerCase(),
    body: String(obj.body ?? obj.Body ?? ''),
    sentAt: String(obj.sent_at ?? obj.sentAt ?? new Date().toISOString()),
  };
}

interface PodChatWindowProps {
  podId: string;
  currentUserId: string;
  authToken: string;
  isMinimized: boolean;
  onClose: (podId: string) => void;
  onMinimize: (podId: string) => void;
  style?: React.CSSProperties;
}

const PodChatWindow: React.FC<PodChatWindowProps> = ({
  podId,
  currentUserId,
  authToken,
  isMinimized,
  onClose,
  onMinimize,
  style,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const applicationClient = useApiClient('applicationClient');
  const podService = useMemo(() => new PodService(applicationClient), [applicationClient]);
  const podHubService = useMemo(() => getPodHubService(authToken), [authToken]);

  const [pod, setPod] = useState<IPodResponse | null>(null);
  const [messages, setMessages] = useState<IPodMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const markAsRead = useCallback(() => {
    dispatch(clearPodUnreadCount(podId));
    void podService.markPodAsRead(podId).catch(error => {
      console.error('PodChatWindow: markPodAsRead error', error);
    });
  }, [dispatch, podId, podService]);

  useEffect(() => {
    void podHubService.start();
  }, [podHubService]);

  // Register this pod as "focused" whenever the window is open (not minimized),
  // so PodChatManager's single global listener treats incoming messages as
  // already-read instead of bumping the unread badge. Mark-as-read once on
  // open/un-minimize; the global listener keeps it read for later messages.
  useEffect(() => {
    if (isMinimized) return;

    addFocusedPod(podId);
    markAsRead();

    return () => removeFocusedPod(podId);
  }, [isMinimized, markAsRead, podId]);

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      try {
        setLoading(true);
        const [podData, messagesResponse] = await Promise.all([
          podService.getPodById(podId),
          applicationClient.get<{ items: unknown[] }>(`/pods/${podId}/messages`, { params: { limit: 40 } }),
        ]);

        if (!isActive) return;

        setPod(podData);
        setMessages((messagesResponse.data.items ?? []).map(normaliseMessage).reverse());
      } catch (error) {
        console.error('PodChatWindow: load error', error);
      } finally {
        if (isActive) setLoading(false);
      }
    };

    void load();

    return () => { isActive = false; };
  }, [applicationClient, podId, podService]);

  useEffect(() => {
    if (!loading) scrollToBottom();
  }, [loading, scrollToBottom]);

  useEffect(() => {
    const unsub = podHubService.on('message.created', (payload: PodMessageCreatedPayload) => {
      if (payload.pod_id && payload.pod_id !== podId) return;

      setMessages(current => [...current, normaliseMessage(payload)]);
      setTimeout(scrollToBottom, 60);
    });

    return unsub;
  }, [podHubService, podId, scrollToBottom]);

  const handleSend = useCallback(async () => {
    const body = input.trim();
    if (!body || sending) return;

    setInput('');
    setSending(true);

    try {
      const response = await podService.sendMessage(podId, body);
      setMessages(current => [...current, normaliseMessage(response)]);
      setTimeout(scrollToBottom, 60);
    } catch (error) {
      console.error('PodChatWindow: send error', error);
      setInput(body);
    } finally {
      setSending(false);
    }
  }, [input, podId, podService, scrollToBottom, sending]);

  const rightOffset = style?.right ?? 120;

  if (isMinimized) {
    return (
      <Box
        onClick={() => onMinimize(podId)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: rightOffset,
          width: 64,
          height: 64,
          minWidth: 64,
          minHeight: 64,
          maxWidth: 64,
          maxHeight: 64,
          cursor: 'pointer',
          userSelect: 'none',
          zIndex: 1500,
          borderRadius: '50% !important',
          overflow: 'hidden',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        }}
      >
        <Avatar
          src={pod?.imageUrl}
          sx={{
            width: 64,
            height: 64,
            minWidth: 64,
            minHeight: 64,
            maxWidth: 64,
            maxHeight: 64,
            border: '3px solid',
            borderColor: 'primary.main',
            borderRadius: '50% !important',
            overflow: 'hidden',
            background: getAvatarGradient(pod?.id || pod?.name || '?'),
          }}
        >
          {pod?.name?.charAt(0).toUpperCase() ?? '?'}
        </Avatar>
      </Box>
    );
  }

  return (
    <Paper
      elevation={isMobile ? 0 : 6}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: rightOffset,
        width: 360,
        height: 420,
        maxWidth: '92vw',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        zIndex: 1500,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: isMobile ? 2 : 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Avatar
          src={pod?.imageUrl}
          sx={{ width: 36, height: 36, background: getAvatarGradient(pod?.id || pod?.name || '?'), fontSize: '0.9rem' }}
        >
          {pod?.name?.charAt(0).toUpperCase() ?? '?'}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
            {pod?.name ?? 'Pod'}
          </Typography>
          <Typography
            variant="caption"
            onClick={() => pod && navigate(`/pods/${pod.slug}`)}
            sx={{ opacity: 0.85, cursor: pod ? 'pointer' : 'default', textDecoration: pod ? 'underline' : 'none' }}
          >
            {pod ? `${pod.memberCount} member${pod.memberCount === 1 ? '' : 's'} · Open Pod` : 'Loading…'}
          </Typography>
        </Box>

        <IconButton size="small" onClick={() => onMinimize(podId)} sx={{ color: 'inherit' }} aria-label="Minimize">
          <RemoveIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onClose(podId)} sx={{ color: 'inherit' }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column' }}>
          {messages.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            messages.map(msg => (
              <ChatMessage key={msg.id} message={msg} currentUserId={currentUserId} podId={podId} podService={podService} podHubService={podHubService} />
            ))
          )}
          <div ref={bottomRef} />
        </Box>
      )}

      <Box sx={{ px: 1.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={`Message ${pod?.name ?? 'the pod'}…`}
          value={input}
          onChange={event => setInput(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void handleSend();
            }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSend} disabled={!input.trim() || sending} size="small" aria-label="Send message">
                    {sending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
      </Box>
    </Paper>
  );
};

export default PodChatWindow;
